import { useState, useRef, useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useNavigation } from 'react-day-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/enhanced-button';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button-variants';
import { format, parseISO } from 'date-fns';
import { DATE_FILTER_OPTIONS, DateFilterKey, resolveDateFilter } from '@/utils/dateFilters';

interface DateFilterProps {
  value: DateFilterKey | '';
  customFrom?: Date;
  customTo?: Date;
  onChange: (key: DateFilterKey | '', from?: Date, to?: Date) => void;
  label?: string;
  placeholder?: string;
  width?: string;
  /** When false, hides the "Any period" empty option. Default: true. */
  showEmpty?: boolean;
  /** When true, custom From/To pickers render below the select instead of inline. */
  stackCustom?: boolean;
  /** When "left", custom From/To pickers render to the left of the select. Default: right. */
  pickerSide?: 'left' | 'right';
}

export const DateFilter = ({
  value,
  customFrom,
  customTo,
  onChange,
  label = 'Date',
  placeholder = 'Any period',
  width = 'w-[180px]',
  showEmpty = true,
  stackCustom = false,
  pickerSide = 'right',
}: DateFilterProps) => {
  const resolved = value ? resolveDateFilter(value, customFrom, customTo) : undefined;
  const dateRangeLabel = resolved
    ? `${format(parseISO(resolved.startDate), 'M/d/yyyy')} – ${format(parseISO(resolved.endDate), 'M/d/yyyy')}`
    : null;

  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  const today = useMemo(() => new Date(), []);

  // Stable ref for the "Today" button click — captures latest props without recreating Caption
  const todayClickRef = useRef(() => {});
  todayClickRef.current = () => {
    const d = new Date();
    onChange('custom', customFrom, d);
    setToOpen(false);
  };

  // Custom caption for the "To" calendar with a "Today" button next to the next-month arrow
  const TodayCaption = useMemo(() => {
    return function TodayCaptionComponent({ displayMonth }: { displayMonth: Date }) {
      const { goToMonth, nextMonth, previousMonth } = useNavigation();
      return (
        <div className="flex justify-center pt-1 relative items-center">
          <button
            type="button"
            className={cn(
              buttonVariants({ variant: 'outline' }),
              'absolute left-1 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
            )}
            onClick={() => previousMonth && goToMonth(previousMonth)}
            disabled={!previousMonth}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium">{format(displayMonth, 'MMMM yyyy')}</span>
          <div className="absolute right-1 flex items-center gap-1">
            <button
              type="button"
              className="text-[10px] px-1.5 py-0.5 rounded border border-input bg-background hover:bg-accent hover:text-accent-foreground leading-none h-5"
              onClick={() => todayClickRef.current()}
            >
              Today
            </button>
            <button
              type="button"
              className={cn(
                buttonVariants({ variant: 'outline' }),
                'h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
              )}
              onClick={() => nextMonth && goToMonth(nextMonth)}
              disabled={!nextMonth}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      );
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customPickers = value === 'custom' && (
    <div className={stackCustom ? "flex gap-1 mt-1" : "contents"}>
      <fieldset className="border border-input rounded-md w-[110px]">
        <legend className="ml-2 px-1 text-xs text-muted-foreground leading-none">From</legend>
        <Popover open={fromOpen} onOpenChange={setFromOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" className="w-full h-8 mt-1 mb-1 justify-start text-left font-normal text-xs px-2">
              {customFrom ? format(customFrom, 'MM/dd/yy') : 'Select'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={customFrom}
              onSelect={(d) => { onChange('custom', d, customTo); setFromOpen(false); }}
              disabled={{ after: customTo ?? today }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </fieldset>
      <fieldset className="border border-input rounded-md w-[110px]">
        <legend className="ml-2 px-1 text-xs text-muted-foreground leading-none">To</legend>
        <Popover open={toOpen} onOpenChange={setToOpen}>
          <PopoverTrigger asChild>
            <Button variant="ghost" className="w-full h-8 mt-1 mb-1 justify-start text-left font-normal text-xs px-2">
              {customTo ? format(customTo, 'MM/dd/yy') : 'Select'}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={customTo}
              onSelect={(d) => { onChange('custom', customFrom, d); setToOpen(false); }}
              disabled={{ after: today }}
              toMonth={today}
              components={{ Caption: TodayCaption }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </fieldset>
    </div>
  );

  if (stackCustom) {
    return (
      <div className="flex flex-col gap-0">
        <fieldset className={`border border-input rounded-md ${width}`}>
          {label && (
            <legend className="ml-2 px-1 text-xs text-muted-foreground leading-none">{label}</legend>
          )}
          <Select
            value={value === '' ? '__all__' : value}
            onValueChange={(v) => onChange((v === '__all__' ? '' : v) as DateFilterKey | '', customFrom, customTo)}
          >
            <SelectTrigger className="w-full border-0 shadow-none h-8 mt-1 focus:ring-0 focus:ring-offset-0 text-xs md:text-sm">
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {showEmpty && <SelectItem value="__all__">Any period</SelectItem>}
              {DATE_FILTER_OPTIONS.map((opt) => (
                <SelectItem key={opt.key} value={opt.key}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {dateRangeLabel && value !== 'custom' && (
            <p className="text-[10px] text-muted-foreground px-3 pb-1.5 leading-none">{dateRangeLabel}</p>
          )}
        </fieldset>
        {customPickers}
      </div>
    );
  }

  const select = (
    <fieldset className={`border border-input rounded-md ${width}`}>
      {label && (
        <legend className="ml-2 px-1 text-xs text-muted-foreground leading-none">{label}</legend>
      )}
      <Select
        value={value === '' ? '__all__' : value}
        onValueChange={(v) => onChange((v === '__all__' ? '' : v) as DateFilterKey | '', customFrom, customTo)}
      >
        <SelectTrigger className="w-full border-0 shadow-none h-8 mt-1 focus:ring-0 focus:ring-offset-0 text-xs md:text-sm">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {showEmpty && <SelectItem value="__all__">Any period</SelectItem>}
          {DATE_FILTER_OPTIONS.map((opt) => (
            <SelectItem key={opt.key} value={opt.key}>{opt.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {dateRangeLabel && value !== 'custom' && (
        <p className="text-[10px] text-muted-foreground px-3 pb-1.5 leading-none">{dateRangeLabel}</p>
      )}
    </fieldset>
  );

  return (
    <>
      {pickerSide === 'left' && customPickers}
      {select}
      {pickerSide === 'right' && customPickers}
    </>
  );
};
